import { SegmentGroup } from "@ark-ui/react";
import { IconName } from "@zenchef/icomoon";
import Icon from "../Icon/Icon";
import {
  aromaSegmentedControl,
  AromaSegmentedControlVariantProps,
} from "@zenchef/styled-system/recipes";
import { createTestId } from "../../utils";

interface BaseItem {
  value: string;
  disabled?: boolean;
}

interface LabelItem extends BaseItem {
  label: string;
}
interface IconItem extends BaseItem {
  icon: IconName;
}

interface LabelIconItem extends LabelItem, IconItem {
  icon: IconName;
}

interface CustomItem extends BaseItem {
  component: React.ReactNode;
}

export interface SegmentedControlProps<
  T extends LabelItem | IconItem | LabelIconItem | CustomItem,
> extends Omit<SegmentGroup.RootProps, "onValueChange">,
    AromaSegmentedControlVariantProps {
  /**
   * The orientation of the segmented control
   * @default "horizontal"
   */
  orientation?: SegmentGroup.RootProps["orientation"];
  /**
   * The list of options to display: each option can have a just an icon or label, or both (in 1 control, all options should be the same format).
   * all options must have a value. An option can be disabled.
   *
   */
  options: T[];
  /**
   * The callback function that is called when the value of the control changes.
   */
  onValueChange?: (details: { value: T["value"] }) => void;
}

/**
 * Segmented Control component uses [SegmentGroup](https://ark-ui.com/react/docs/components/segment-group) from ark-ui.
 *
 * it can be used standalone with `defaultValue` or Controlled with `value` and `onValueChange` props. (see code examples below)
 *
 */
const SegmentedControl = <
  T extends LabelItem | IconItem | LabelIconItem | CustomItem,
>({
  options,
  orientation = "horizontal",
  ...propsWithVariants
}: SegmentedControlProps<T>) => {
  const [variantProps, rootProps] =
    aromaSegmentedControl.splitVariantProps(propsWithVariants);
  const classes = aromaSegmentedControl(variantProps);
  return (
    <SegmentGroup.Root
      className={classes.root}
      orientation={orientation}
      {...rootProps}
    >
      <SegmentGroup.Indicator className={classes.indicator} />
      {options.map((option) => (
        <SegmentGroup.Item
          key={option.value}
          value={option.value}
          className={classes.item}
          disabled={option.disabled}
          data-icon={!("label" in option)}
          data-testid={createTestId({
            testId: `segmented-control-${option.value}`,
            type: "btn",
          })}
        >
          <SegmentGroup.ItemText>
            {"icon" in option ? <Icon name={option.icon} /> : null}
            {"label" in option ? option.label : null}
            {"component" in option ? option.component : null}
          </SegmentGroup.ItemText>
          <SegmentGroup.ItemHiddenInput />
        </SegmentGroup.Item>
      ))}
    </SegmentGroup.Root>
  );
};

export default SegmentedControl;
